import api from './client';

export interface VaultStatus {
  replicationEnabled: boolean;
  localEnabled: boolean;
  remoteIngest: boolean;
  retentionDays: number;
  lastReplication: {
    ok: boolean;
    at: string | null;
    backupId: string | null;
    error: string | null;
  };
}

export interface VaultBackupObject {
  id: string;
  instanceId: string;
  backupId: string;
  sourceName: string;
  checksumSha256: string;
  sizeBytes: string;
  takenAt: string;
  retentionUntil?: string | null;
  storageKey: string;
  storageBackend: string;
  status: string;
}

export interface VaultRestoreJob {
  id: string;
  vaultBackupId: string;
  sourceBackupId: string;
  instanceId: string;
  targetNote?: string | null;
  status: string;
  requesterEmail: string;
  approverEmail?: string | null;
  breakglass: boolean;
  runbookSteps: Array<{ at: string; step: string; detail?: string }>;
  lastError?: string | null;
  createdAt: string;
}

export interface PortalSnapshot {
  instance: {
    id: string;
    name: string;
    environment: string;
    plan: string;
  };
  supportBaseUrl: string | null;
  backupReplication: string;
  latestVaultBackups: Array<{
    backupId: string;
    takenAt: string;
    sizeBytes: string;
    status: string;
    checksumSha256: string;
  }>;
  dualControlRestore: boolean;
  note: string;
}

export const vaultApi = {
  status: async () => {
    const { data } = await api.get<VaultStatus>('vault/admin/status');
    return data;
  },
  listBackups: async () => {
    const { data } = await api.get<VaultBackupObject[]>('vault/admin/backups');
    return data;
  },
  listRestores: async () => {
    const { data } = await api.get<VaultRestoreJob[]>('vault/admin/restores');
    return data;
  },
  requestRestore: async (body: {
    vaultBackupId: string;
    targetNote?: string;
    breakglassToken?: string;
  }) => {
    const { data } = await api.post<VaultRestoreJob>('vault/admin/restores', body);
    return data;
  },
  approveRestore: async (id: string) => {
    const { data } = await api.post<VaultRestoreJob>(`vault/admin/restores/${id}/approve`);
    return data;
  },
  executeRestore: async (id: string) => {
    const { data } = await api.post<{
      job: VaultRestoreJob;
      runbook: VaultRestoreJob['runbookSteps'];
    }>(`vault/admin/restores/${id}/execute`);
    return data;
  },
  runObjectMirror: async () => {
    const { data } = await api.post<{ mirrored: number; failed: number }>(
      'vault/admin/mirrors/run',
    );
    return data;
  },
  portalMe: async () => {
    const { data } = await api.get<PortalSnapshot>('portal/me');
    return data;
  },
};
